import React, { useState, useEffect, useMemo } from 'react';
import { db } from "./firebase";
import { collection, query, orderBy, limit, startAfter, getDocs, doc, getDoc } from "firebase/firestore";



function ObservershipsPage() {
  const [observerships, setObserverships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);   // initial page load only
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [allLoaded, setAllLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination refs (avoid stale closure in IntersectionObserver)
  const lastDocRef = React.useRef(null);
  const allLoadedRef = React.useRef(false);
  const loadingMoreRef = React.useRef(false);
  const loadMoreRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const tableContainerRef = React.useRef(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    specialties: [],
    types: [],
    states: [],
    feeRanges: ['Free', '$1-500', '$501-1000', '$1001-2000', '$2001-3000', '$3000+']
  });

  // Consolidated filter state
  const [selectedFilters, setSelectedFilters] = useState({
    specialty: "",
    type: "",
    state: "",
    feeRange: "",
    search: ""
  });


  const PAGE_SIZE = 20;
  const CACHE_KEY = 'obs_v3';
  const CACHE_TTL = 2 * 60 * 60 * 1000;

  const buildObsQuery = (afterDoc = null) => {
    const col = collection(db, "observershipPrograms");
    const constraints = [orderBy("_idx"), limit(PAGE_SIZE)];
    if (afterDoc) constraints.push(startAfter(afterDoc));
    return query(col, ...constraints);
  };

  const loadInitial = async () => {
    try {
      // Try localStorage cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { rows, fo, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          const versionDoc = await getDoc(doc(db, "dataVersions", "observerships"));
          const serverUpdatedAt = versionDoc.exists() ? versionDoc.data().updatedAt : 0;
          if (serverUpdatedAt <= ts) {
            setObserverships(rows);
            setFilterOptions(fo);
            setIsLoading(false);
            // Mark all loaded from cache (first page only cached, keep sentinel active)
            return;
          }
          localStorage.removeItem(CACHE_KEY);
        }
      }

      // Load filter options from Firestore meta doc
      const metaSnap = await getDoc(doc(db, "observershipMeta", "filters"));
      if (metaSnap.exists()) {
        const meta = metaSnap.data();
        setFilterOptions({
          specialties: meta.specialties || [],
          types: meta.types || [],
          states: meta.states || [],
          feeRanges: meta.feeRanges || ['Free', '$1-500', '$501-1000', '$1001-2000', '$2001-3000', '$3000+']
        });
        setTotalCount(meta.totalCount || 0);
      }

      // Load first page of programs
      const snap = await getDocs(buildObsQuery());
      if (snap.empty) {
        setIsLoading(false);
        setAllLoaded(true);
        allLoadedRef.current = true;
        return;
      }

      const rows = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
      setObserverships(rows);

      const newLastDoc = snap.docs[snap.docs.length - 1];
      setLastDoc(newLastDoc);
      lastDocRef.current = newLastDoc;

      if (snap.docs.length < PAGE_SIZE) {
        setAllLoaded(true);
        allLoadedRef.current = true;
      }

      setIsLoading(false);

      // Cache first page + filter options
      try {
        if (metaSnap.exists()) {
          const fo = {
            specialties: metaSnap.data().specialties || [],
            types: metaSnap.data().types || [],
            states: metaSnap.data().states || [],
            feeRanges: metaSnap.data().feeRanges || ['Free', '$1-500', '$501-1000', '$1001-2000', '$2001-3000', '$3000+']
          };
          localStorage.setItem(CACHE_KEY, JSON.stringify({ rows, fo, ts: Date.now() }));
        }
      } catch (_) { /* quota */ }

    } catch (err) {
      console.error("Error loading observership data:", err);
      setError(`Failed to load observership data: ${err.message}`);
      setIsLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMoreRef.current || allLoadedRef.current || !lastDocRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const snap = await getDocs(buildObsQuery(lastDocRef.current));
      if (!snap.empty) {
        const newRows = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
        setObserverships(prev => [...prev, ...newRows]);
        const newLastDoc = snap.docs[snap.docs.length - 1];
        setLastDoc(newLastDoc);
        lastDocRef.current = newLastDoc;
        if (snap.docs.length < PAGE_SIZE) {
          setAllLoaded(true);
          allLoadedRef.current = true;
        }
      } else {
        setAllLoaded(true);
        allLoadedRef.current = true;
      }
    } catch (err) {
      console.error("loadMore error:", err);
    }
    loadingMoreRef.current = false;
    setLoadingMore(false);
  };

  // Keep refs in sync with state
  useEffect(() => { lastDocRef.current = lastDoc; }, [lastDoc]);
  useEffect(() => { allLoadedRef.current = allLoaded; }, [allLoaded]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { loadMoreRef.current = loadMore; }); // always-current, no deps

  // Auto-load all pages without requiring scroll
  useEffect(() => {
    if (isLoading || allLoaded || !lastDoc) return;
    // Keep loading pages until all data is fetched
    const autoLoad = async () => {
      while (!allLoadedRef.current && lastDocRef.current && !loadingMoreRef.current) {
        await loadMoreRef.current?.();
        // Small delay to avoid hammering Firestore
        await new Promise(res => setTimeout(res, 100));
      }
    };
    autoLoad();
  }, [isLoading, lastDoc, allLoaded]);

  // Helper functions
  const formatFee = (fee) => {
    if (!fee || fee === 'Not specified') return 'Not specified';
    if (typeof fee === 'number') return `$${fee}`;
    return fee.toString();
  };

  const getFeeRange = (fee) => {
    if (!fee || fee === 'Not specified' || fee.toString().toLowerCase().includes('free')) return 'Free';
    
    const numericFee = parseFloat(fee.toString().replace(/[^0-9.]/g, ''));
    if (isNaN(numericFee)) return 'Unknown';
    
    if (numericFee <= 500) return '$1-500';
    if (numericFee <= 1000) return '$501-1000';
    if (numericFee <= 2000) return '$1001-2000';
    if (numericFee <= 3000) return '$2001-3000';
    return '$3000+';
  };

  const convertTextToLinks = (text) => {
    if (!text || text === "—" || text === "" || text === "N/A") return text;
    
    // If it's already a valid URL, make it clickable
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Also handle URLs without protocol
    const urlWithoutProtocolRegex = /(www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/g;
    
    let processedText = text.toString();
    
    // First handle URLs with protocol
    processedText = processedText.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium" onclick="event.stopPropagation()">$1</a>');

    // Then handle URLs without protocol (but avoid double-processing)
    if (!urlRegex.test(text)) {
      processedText = processedText.replace(urlWithoutProtocolRegex, '<a href="http://$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium" onclick="event.stopPropagation()">$1</a>');
    }
    
    return processedText;
  };

  // Filter configuration function similar to TablePage
  const getFilterConfig = () => {
    return [
      {
        key: 'specialty',
        icon: '⚕️',
        label: 'Specialty',
        options: filterOptions.specialties,
        color: 'blue',
        description: 'Filter by medical specialty'
      },
      {
        key: 'type',
        icon: '🏥',
        label: 'Program Type',
        options: filterOptions.types,
        color: 'green',
        description: 'Filter by type of clinical experience'
      },
      {
        key: 'state',
        icon: '🗺️',
        label: 'State',
        options: filterOptions.states,
        color: 'purple',
        description: 'Filter by US state location'
      },
      {
        key: 'feeRange',
        icon: '💰',
        label: 'Fee Range',
        options: filterOptions.feeRanges,
        color: 'orange',
        description: 'Filter by program cost'
      }
    ].filter(f => f.options && f.options.length > 0);
  };

  // Apply filters function
  const applyFilters = React.useCallback((data) => {
    return data.filter(obs => {
      const matchesSearch = !selectedFilters.search || 
        obs.name.toLowerCase().includes(selectedFilters.search.toLowerCase()) ||
        obs.cityState.toLowerCase().includes(selectedFilters.search.toLowerCase()) ||
        obs.specialty.toLowerCase().includes(selectedFilters.search.toLowerCase());
      
      const matchesSpecialty = !selectedFilters.specialty || obs.specialty === selectedFilters.specialty;
      const matchesType = !selectedFilters.type || obs.type === selectedFilters.type;
      const matchesState = !selectedFilters.state || obs.cityState.includes(selectedFilters.state);
      const matchesFeeRange = !selectedFilters.feeRange || getFeeRange(obs.fee) === selectedFilters.feeRange;

      return matchesSearch && matchesSpecialty && matchesType && matchesState && matchesFeeRange;
    });
  }, [selectedFilters]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = applyFilters(observerships);

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';
        
        if (sortConfig.key === 'fee') {
          const aNum = parseFloat(aVal.toString().replace(/[^0-9.]/g, '')) || 0;
          const bNum = parseFloat(bVal.toString().replace(/[^0-9.]/g, '')) || 0;
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    return filtered;
  }, [observerships, applyFilters, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const clearFilters = () => {
    setSelectedFilters({
      specialty: "",
      type: "",
      state: "",
      feeRange: "",
      search: ""
    });
  };

  const activeFiltersCount = Object.values(selectedFilters).filter(f => f).length;

  useEffect(() => {
    loadInitial();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-12 flex flex-col items-center justify-center">
        <div className="w-16 h-16 bg-black/5 dark:bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-black dark:text-white mb-2">Data Not Available</h3>
        <p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-5 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98]"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.back()}
            className="p-2.5 hover:bg-black/[0.06] dark:hover:bg-white/10 rounded-xl transition-all duration-200 active:scale-95"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold text-black dark:text-white tracking-[-0.02em]">Observerships & Electives</h1>
            <div className="flex items-center space-x-3 mt-1">
              <span className="text-[14px] text-gray-500 dark:text-gray-400">{filteredAndSortedData.length} opportunities</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilterModal(true)}
            className="bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-5 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98] shadow-lg shadow-black/10 dark:shadow-black/20 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filter
            {activeFiltersCount > 0 && (
              <span className="ml-2 bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>


        </div>
      </div>

      {/* Active Filters Bar */}
      {activeFiltersCount > 0 && (
        <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-black dark:text-white flex items-center">
              <svg className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Active Filters ({activeFiltersCount})
            </h3>
            <button
              onClick={clearFilters}
              className="text-xs text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {getFilterConfig().map((filter, index) => {
              const selectedValue = selectedFilters[filter.key];
              if (!selectedValue) return null;

              return (
                <div
                  key={index}
                  className="inline-flex items-center bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 rounded-full px-3 py-1.5 text-sm"
                >
                  <span className="mr-1.5">{filter.icon}</span>
                  <span className="text-gray-700 dark:text-gray-200 font-medium mr-2">{filter.label}: {selectedValue}</span>
                  <button
                    onClick={() => setSelectedFilters(prev => ({ ...prev, [filter.key]: "" }))}
                    className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white ml-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}
            {selectedFilters.search && (
              <div className="inline-flex items-center bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 rounded-full px-3 py-1.5 text-sm">
                <span className="mr-1.5">🔍</span>
                <span className="text-gray-700 dark:text-gray-200 font-medium mr-2">Search: {selectedFilters.search}</span>
                <button
                  onClick={() => setSelectedFilters(prev => ({ ...prev, search: "" }))}
                  className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white ml-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="mt-3 text-[13px] text-gray-500 dark:text-gray-400">
            Showing {filteredAndSortedData.length} opportunities that match your criteria
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search institutions, cities, specialties..."
            className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800 border border-black/10 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 focus:border-transparent text-black dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            value={selectedFilters.search}
            onChange={(e) => setSelectedFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>

      {/* Results Summary */}
      <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 border border-black/8 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-black dark:text-white font-medium">
              Showing {filteredAndSortedData.length} of {totalCount || observerships.length} opportunities
            </span>
          </div>
          {activeFiltersCount > 0 && (
            <span className="text-[13px] text-gray-500 dark:text-gray-400">
              Filtered by {activeFiltersCount} criteria
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-black/8 dark:border-gray-700 overflow-hidden">
        <div className="bg-black/[0.03] dark:bg-white/5 px-4 sm:px-6 py-3 border-b border-black/8 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-black dark:text-white">Observership Opportunities</h3>
          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
            <span>{filteredAndSortedData.length} opportunities</span>
            {activeFiltersCount > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  {activeFiltersCount} filter(s) active
                </div>
              </>
            )}
          </div>
        </div>

        <div className="overflow-x-auto" ref={tableContainerRef}>
          <table className="min-w-full divide-y divide-black/8 dark:divide-gray-700">
            <thead className="bg-black/[0.03] dark:bg-white/5">
              <tr>
                {['name', 'cityState', 'specialty', 'type', 'fee', 'duration', 'requirements', 'contact', 'notes'].map((key) => (
                  <th
                    key={key}
                    className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-black/[0.06] dark:hover:bg-white/10 transition-colors group"
                    onClick={() => handleSort(key)}
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        {key === 'name' && 'Institution'}
                        {key === 'cityState' && 'Location'}
                        {key === 'specialty' && 'Specialty'}
                        {key === 'type' && 'Type'}
                        {key === 'fee' && 'Fee'}
                        {key === 'duration' && 'Duration'}
                        {key === 'requirements' && 'Requirements'}
                        {key === 'contact' && 'Contact'}
                        {key === 'notes' && 'Notes'}
                      </span>
                      <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-black/8 dark:divide-gray-700">
              {filteredAndSortedData.map((obs, index) => (
                <tr key={obs.id || index} className="hover:bg-black/[0.03] dark:hover:bg-white/5 transition-all duration-200 group cursor-pointer">
                  {/* Institution Name */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-black dark:text-white">
                    {obs.name || "—"}
                  </td>

                  {/* Location */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.cityState || "—"}
                  </td>

                  {/* Specialty */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.specialty || "—"}
                  </td>

                  {/* Type */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.type || "—"}
                  </td>

                  {/* Fee */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {formatFee(obs.fee)}
                  </td>

                  {/* Duration */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.duration || "—"}
                  </td>

                  {/* Requirements */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.requirements || "—"}
                  </td>

                  {/* Contact */}
                  <td
                    className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 cursor-default"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-1">
                      {obs.website && (
                        <div dangerouslySetInnerHTML={{ __html: convertTextToLinks(obs.website) }} />
                      )}
                      {obs.contactEmail && (
                        <div>
                          <a
                            href={`mailto:${obs.contactEmail}`}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {obs.contactEmail}
                          </a>
                        </div>
                      )}
                      {obs.contactPerson && (
                        <div>{obs.contactPerson}</div>
                      )}
                      {!obs.website && !obs.contactEmail && !obs.contactPerson && "—"}
                    </div>
                  </td>

                  {/* Notes */}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-700 dark:text-gray-300 group-hover:text-black dark:group-hover:text-white transition-colors">
                    {obs.notes || "—"}
                  </td>
                </tr>
              ))}
              {/* Pagination sentinel */}
              {!allLoaded && (
                <tr>
                  <td colSpan={9} className="px-4 sm:px-6 py-2 text-center text-xs text-gray-500 dark:text-gray-400">
                    <div ref={sentinelRef} style={{ height: 1 }} />
                    {loadingMore && <span>Loading more…</span>}
                  </td>
                </tr>
              )}
              {/* Initial loading skeleton rows */}
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-${i}`} className="animate-pulse">
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j} className="px-4 sm:px-6 py-4">
                      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-black dark:text-white mb-2">No observerships found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Try adjusting your search terms or filters</p>
            <button
              onClick={clearFilters}
              className="bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-5 py-3 rounded-full font-medium transition-all duration-200 active:scale-[0.98]"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Enhanced Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-4xl w-full shadow-2xl max-h-[90vh] overflow-y-auto border border-black/8 dark:border-gray-700">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-r from-gray-700 to-gray-900 dark:from-gray-600 dark:to-gray-800 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-black dark:text-white mb-2">Filter Observerships</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Filter opportunities based on your preferences to find the best matches
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {getFilterConfig().map((filter) => {
                const selectedValue = selectedFilters[filter.key];

                return (
                  <div
                    key={filter.key}
                    className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                      selectedValue
                        ? "bg-black/[0.04] dark:bg-white/10 border-black/20 dark:border-gray-500"
                        : "bg-black/[0.02] dark:bg-white/5 border-black/8 dark:border-gray-700 hover:bg-black/[0.03] dark:hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">{filter.icon}</span>
                      <div>
                        <h4 className="font-semibold text-lg text-black dark:text-white">{filter.label}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{filter.description}</p>
                      </div>
                    </div>

                    <select
                      value={selectedValue}
                      onChange={(e) => setSelectedFilters(prev => ({
                        ...prev,
                        [filter.key]: e.target.value
                      }))}
                      className="w-full p-3 bg-white dark:bg-gray-800 border border-black/10 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/20 dark:focus:ring-white/20 focus:border-transparent text-black dark:text-white"
                    >
                      <option value="">All {filter.label.toLowerCase()}</option>
                      {filter.options.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            {/* Active Filters Summary */}
            {activeFiltersCount > 0 && (
              <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-4 mb-6 border border-black/8 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-black dark:text-white mb-2">Active Filters:</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedFilters).map(([key, value]) => {
                    if (!value || key === 'search') return null;
                    const filter = getFilterConfig().find(f => f.key === key);
                    if (!filter) return null;
                    return (
                      <div key={key} className="inline-flex items-center bg-white dark:bg-gray-700 border border-black/10 dark:border-gray-600 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.icon}</span>
                        <span className="text-gray-700 dark:text-gray-200 mr-2">{filter.label}: {value}</span>
                        <button
                          onClick={() => setSelectedFilters(prev => ({ ...prev, [key]: "" }))}
                          className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={clearFilters}
                className="flex-1 bg-black/[0.06] dark:bg-white/10 hover:bg-black/[0.08] dark:hover:bg-white/15 text-gray-700 dark:text-gray-200 px-6 py-3 rounded-xl font-medium transition-colors border border-black/8 dark:border-gray-600"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 bg-gray-500 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-500 text-white px-6 py-3 rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 bg-gradient-to-r from-gray-800 to-black dark:from-gray-600 dark:to-gray-800 hover:from-gray-700 hover:to-gray-900 dark:hover:from-gray-500 dark:hover:to-gray-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 active:scale-[0.98] shadow-lg"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="bg-black/[0.02] dark:bg-white/5 rounded-2xl p-6 border border-black/8 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-black dark:text-white mb-2">About This Database</h3>
        <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">
          This comprehensive database contains {totalCount || observerships.length} observership and elective opportunities from US medical institutions.
          The data includes clinical electives, observerships, externships, and research opportunities across various specialties.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white dark:bg-gray-800/50 p-3 rounded-xl border border-black/8 dark:border-gray-700">
            <div className="font-semibold text-black dark:text-white mb-1">Tips for Success</div>
            <ul className="text-gray-700 dark:text-gray-300 space-y-1">
              <li>• Apply early (6-12 months in advance)</li>
              <li>• Check requirements carefully</li>
              <li>• Have strong USMLE scores ready</li>
            </ul>
          </div>
          <div className="bg-white dark:bg-gray-800/50 p-3 rounded-xl border border-black/8 dark:border-gray-700">
            <div className="font-semibold text-black dark:text-white mb-1">Common Requirements</div>
            <ul className="text-gray-700 dark:text-gray-300 space-y-1">
              <li>• USMLE Step 1 & 2 scores</li>
              <li>• TOEFL/IELTS scores</li>
              <li>• Letters of recommendation</li>
            </ul>
          </div>
          <div className="bg-white dark:bg-gray-800/50 p-3 rounded-xl border border-black/8 dark:border-gray-700">
            <div className="font-semibold text-black dark:text-white mb-1">Fee Information</div>
            <ul className="text-gray-700 dark:text-gray-300 space-y-1">
              <li>• Fees vary widely ($500-$5000+)</li>
              <li>• Some programs are free</li>
              <li>• Additional costs may apply</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Data sourced from medical student applications and program websites. Always verify current requirements with institutions directly.
        </div>
      </div>
    </div>
  );
}

export default ObservershipsPage;